// CTW-DURMAH-STUDENT-MANUAL-01: Durmah chat/voice, assignments, Mark My Paper.
//
// REDACTION NOTE: the integrity-refusal trigger keywords and the reproducible
// refusal string are deliberately absent from this file and from every other
// manual section. Publishing the trip-wire teaches students how to route
// around the guard. The STANCE is documented; the mechanism is not.

import type { ManualSection } from "../types";

export const coreToolSections: ManualSection[] = [
  /* ---------------------------------------------------------------- */
  /* Talking to Durmah                                                 */
  /* ---------------------------------------------------------------- */

  {
    slug: "talking-to-durmah",
    title: "Talking to Durmah in text",
    summary:
      "How Durmah answers, what it knows about your studies already, and how to read the source note on every legal answer.",
    category: "durmah",
    audience: "public",
    order: 1,
    readTime: "4 min read",
    tags: ["Durmah", "Daily use"],
    keywords: [
      "chat",
      "ask durmah",
      "text chat",
      "how do i ask a question",
      "source note",
      "grounded",
      "does durmah know my modules",
      "conversation",
    ],
    blocks: [
      {
        kind: "para",
        text: "Open Durmah from the launcher at the bottom right of any page. On pages where the launcher hides itself, chat is embedded in the page instead — inside Exam Prep and inside the assignment workflow.",
      },
      {
        kind: "heading",
        id: "what-it-knows",
        text: "What Durmah already knows",
      },
      {
        kind: "para",
        text: "You do not need to re-explain your situation each time. Durmah knows your university, your year, your modules and your upcoming deadlines — and, inside a lecture or an assignment, what you are currently working on.",
      },
      {
        kind: "heading",
        id: "source-notes",
        text: "Reading the source note",
      },
      {
        kind: "para",
        text: "Every substantive legal answer carries a note explaining its own basis. These are worth reading, because they tell you how much weight the answer can bear.",
      },
      {
        kind: "table",
        headers: ["What the note says", "What it means for you"],
        rows: [
          [
            "Constrained to verified UK-law authority and statute layers",
            "Solid ground. Still verify citations against your module materials before assessed work.",
          ],
          [
            "Only partial verified support",
            "Usable as a starting point. Check your reading list before relying on it in assessed work.",
          ],
          [
            "Not enough verified support to answer fully",
            "Durmah is declining rather than guessing. Upload the relevant lecture, reading list or brief and ask again.",
          ],
        ],
      },
      {
        kind: "heading",
        id: "good-questions",
        text: "Questions that get good answers",
      },
      {
        kind: "bullets",
        items: [
          "Explain the difference between these two cases and tell me which one my lecturer actually relied on.",
          "I think the issue here is remoteness. Am I missing an issue?",
          "Here is my structure for this problem question — where is it weakest?",
          "What is the counter-argument to the position I have just taken?",
          "I have four days and three deadlines. Help me plan the week.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Argue with it",
        text: "Durmah is most useful when you push back. Tell it why you think its answer is wrong; ask it to make the strongest case against your own position. Passive question-and-answer gets you information. Argument gets you understanding — and understanding is what the exam tests.",
      },
      {
        kind: "heading",
        id: "history",
        text: "Where your conversations live",
      },
      {
        kind: "para",
        text: "Conversations persist. A general thread follows you around the app, and separate threads attach to each lecture, each assignment and each exam module — so context never bleeds between pieces of work.",
      },
    ],
    related: ["using-durmah-voice", "what-durmah-remembers", "asking-about-a-lecture"],
  },

  {
    slug: "using-durmah-voice",
    title: "Durmah Voice: speaking law out loud",
    summary:
      "Voice exists because articulating legal reasoning aloud is a different skill from writing it — and it is the one seminars, moots and interviews actually test.",
    category: "durmah",
    audience: "public",
    order: 2,
    readTime: "4 min read",
    tags: ["Durmah", "Speak Law"],
    keywords: [
      "voice",
      "speak",
      "talk to durmah",
      "microphone",
      "mic",
      "spoken",
      "oral",
      "moot practice",
      "viva",
      "seminar practice",
      "voice not working",
    ],
    blocks: [
      {
        kind: "steps",
        intro: "Starting a voice session:",
        items: [
          "Open the Durmah panel.",
          "Select Mic. Your browser will ask for microphone permission — allow it.",
          "Start talking. Durmah listens, works out when you have finished a thought, and responds.",
          "Select End when you are done.",
        ],
      },
      {
        kind: "table",
        intro: "The controls:",
        headers: ["Control", "What it does"],
        rows: [
          ["Mic / End", "Start or end the session."],
          ["Mute", "Cut your microphone without ending the session."],
          ["Stop", "Stop Durmah mid-sentence, while it is speaking."],
          ["Speed", "Slow, Normal, Fast or Very Fast. Change it between turns."],
          [
            "Voice",
            "Eight voices with previews. Choose before you start a call.",
          ],
          [
            "Transcript",
            "Opens automatically. Read the turn-by-turn transcript and save it to your library.",
          ],
          ["Minimize", "Keeps the session alive while you use the rest of the app."],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "You can interrupt it",
        text: "Talk over Durmah and it stops and listens — the same way a real conversation works. Anything it was midway through saying is discarded rather than recorded as an answer it gave.",
      },
      {
        kind: "heading",
        id: "what-voice-is-for",
        text: "What voice is genuinely good for",
      },
      {
        kind: "bullets",
        items: [
          "Explaining a case out loud until it actually makes sense.",
          "Rehearsing a seminar contribution before you have to make it.",
          "Being interrogated on doctrine you think you already know.",
          "Talking through an argument before you commit it to writing.",
          "Moot and viva practice, including hostile questioning.",
        ],
      },
      {
        kind: "callout",
        tone: "note",
        title: "Voice is released in stages",
        text: "If voice is not yet enabled for your account you will see a message saying it is in a limited testing stage. That is a rollout message, not a fault, and text chat has the full capability in the meantime.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "If the mic does nothing",
        text: "Check that your browser has microphone permission for Durmah. A browser-level denial cannot be overridden from inside the app — you need to re-grant it in your browser's site settings.",
      },
    ],
    related: ["voice-sessions-and-limits", "talking-to-durmah", "quiz-me"],
  },

  {
    slug: "voice-sessions-and-limits",
    title: "Voice sessions: length, allowance and messages",
    summary:
      "How long a session runs, what your monthly allowance covers, and what each voice message means.",
    category: "durmah",
    audience: "member",
    order: 3,
    readTime: "3 min read",
    tags: ["Durmah", "Limits", "Member"],
    keywords: [
      "voice limit",
      "how long can i talk",
      "voice allowance",
      "voice minutes",
      "session ended",
      "voice quota",
      "voice error",
      "reconnecting",
    ],
    blocks: [
      {
        kind: "table",
        headers: ["Behaviour", "Detail"],
        rows: [
          [
            "Session length",
            "A session runs up to 20 minutes. You are warned about five minutes before the end.",
          ],
          [
            "Quiet spells",
            "After a minute of silence Durmah says it is still listening. After two minutes it closes the session politely.",
          ],
          [
            "Monthly allowance",
            "Voice minutes are subject to a monthly allowance. Text chat is never affected by it.",
          ],
          [
            "One at a time",
            "You can have one voice session open at a time. If you see a message saying you already have one, close the other or wait a couple of minutes.",
          ],
        ],
      },
      {
        kind: "table",
        intro: "Messages you might see, and what to do:",
        headers: ["Message", "What it means"],
        rows: [
          [
            "Microphone access was blocked",
            "Your browser denied the mic. Re-grant it in your browser's site settings.",
          ],
          [
            "Voice is in a limited testing stage",
            "Not yet enabled for your account. Text chat is fully available.",
          ],
          [
            "You've used this month's voice allowance",
            "Monthly cap reached. Text chat is unaffected.",
          ],
          [
            "Voice connection hiccup — reconnecting",
            "A network wobble. It usually recovers by itself.",
          ],
          [
            "Voice couldn't reconnect",
            "Reconnection failed. Start a new session.",
          ],
          [
            "Voice session reached its limit",
            "The session cap. Start another one whenever you like.",
          ],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Get more from your allowance",
        text: "Voice is best spent on the things text cannot do — being questioned aloud, rehearsing an argument, thinking through a problem by talking. Use text for lookups, planning and anything you want to be able to re-read.",
      },
    ],
    related: ["using-durmah-voice", "what-durmah-remembers", "saving-transcripts"],
  },

  {
    slug: "what-durmah-remembers",
    title: "What Durmah remembers about you",
    summary:
      "What is kept, what is deliberately never kept, and how to clear it. Includes the one thing Durmah will refuse to store.",
    category: "durmah",
    audience: "public",
    order: 4,
    readTime: "3 min read",
    tags: ["Durmah", "Privacy"],
    keywords: [
      "memory",
      "does durmah remember",
      "remember this",
      "forget",
      "clear memory",
      "privacy",
      "is my audio recorded",
      "data",
    ],
    blocks: [
      {
        kind: "bullets",
        intro: "Durmah keeps:",
        items: [
          "Your completed conversations and summaries of them.",
          "Your modules and academic context.",
          "Your saved study preferences.",
          "Anything you explicitly ask it to remember.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "Raw voice audio is never stored",
        text: "Voice conversations produce text turns; the audio itself is not retained. And only completed turns are kept — if an answer was interrupted, cancelled or failed, it is not recorded as something Durmah said.",
      },
      {
        kind: "heading",
        id: "asking-it-to-remember",
        text: "Asking it to remember something",
      },
      {
        kind: "para",
        text: "Tell Durmah to remember something and it will, then confirm it has. Ask it later what you asked it to remember and it will tell you.",
      },
      {
        kind: "callout",
        tone: "safety",
        title: "It will refuse to store a secret",
        text: "If something looks like a password, a one-time code, a PIN, a bank or account number, or an identity document number, Durmah declines to store it and tells you why. Please do not try to work around this — it exists to protect you.",
      },
      {
        kind: "heading",
        id: "clearing",
        text: "Clearing it",
      },
      {
        kind: "para",
        text: "You can clear a single conversation or your entire Durmah memory from the chat panel. Clearing is thorough — stored messages, summaries, notes and saved voice transcripts all go. For a formal account-level data deletion request, use the privacy page instead.",
      },
    ],
    related: ["privacy-and-your-data", "saving-transcripts", "talking-to-durmah"],
  },

  {
    slug: "saving-transcripts",
    title: "Saving and organising your transcripts",
    summary:
      "Voice sessions can be archived properly — folders, search, pinning, and resuming a quiz from its transcript.",
    category: "durmah",
    audience: "member",
    order: 5,
    readTime: "2 min read",
    tags: ["Durmah", "Member"],
    keywords: [
      "transcript",
      "save transcript",
      "transcript library",
      "folders",
      "find old conversation",
      "search my transcripts",
      "pin",
    ],
    blocks: [
      {
        kind: "bullets",
        intro: "My Voice Transcripts is the most developed of the personal libraries:",
        items: [
          "A folder tree you can create, rename, nest and delete.",
          "All Transcripts and Pinned Items views.",
          "Search across your transcripts, with match highlighting and next/previous navigation inside a transcript.",
          "Grid or list view, sorted by most recent, oldest or title.",
          "Pin, move to folder, copy full text, and delete.",
          "An automatic summary alongside the full turn-by-turn transcript.",
          "Resume Quiz, for transcripts that came from a quiz session.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Save the ones where you were wrong",
        text: "The most useful transcripts to keep are the sessions where Durmah dismantled your argument. Re-read one the week before an exam and you will find the misunderstanding is still worth fixing.",
      },
    ],
    related: ["using-durmah-voice", "what-durmah-remembers", "quiz-me"],
  },

  /* ---------------------------------------------------------------- */
  /* Assignments                                                       */
  /* ---------------------------------------------------------------- */

  {
    slug: "assignment-workflow",
    title: "The six-stage assignment workflow",
    summary:
      "Where a piece of assessed work lives from the moment you receive the brief to the moment before you submit — with Durmah as a tutor, not a writer.",
    category: "assignments",
    audience: "public",
    order: 1,
    readTime: "5 min read",
    tags: ["Assignments", "Flagship"],
    keywords: [
      "assignment",
      "essay",
      "coursework",
      "problem question",
      "how do i start an assignment",
      "assignment help",
      "stages",
      "irac",
      "structure my essay",
    ],
    blocks: [
      {
        kind: "quote",
        attribution: "The standing note on the Assignment Hub",
        text: "Durmah is a tutor, not a writer. It can help you plan, test arguments, and explain doctrine — it will not write or rewrite your assessed work for you. Verify every case citation and statutory reference against your module materials and primary sources.",
      },
      {
        kind: "heading",
        id: "starting",
        text: "Starting",
      },
      {
        kind: "para",
        text: "Add the brief — upload it as a PDF or DOCX, or paste the question text. Durmah reads it, identifies the command words, works out what the question is actually asking, and opens the workflow.",
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "Why the brief is required",
        text: "Without the actual question, the early stages are deliberately blocked. Guidance built on a guess about what your assignment asks is worse than no guidance — it is confidently wrong in a way that costs you marks. Add the brief and the whole workflow becomes specific to your task.",
      },
      {
        kind: "heading",
        id: "the-stages",
        text: "The six stages",
      },
      {
        kind: "table",
        headers: ["Stage", "What happens", "What you leave with"],
        rows: [
          [
            "1 · Understand brief",
            "Durmah interrogates the question with you. It will not move on until you have named the sub-issues and identified the hardest one.",
            "A clear map of what the question asks",
          ],
          [
            "2 · Research",
            "Guidance towards the leading case, the statute and the textbook — then the refinements, the opposing authority and the commentary. Durmah tells you where to look; it does not do the reading for you.",
            "A source list you built",
          ],
          [
            "3 · Outline",
            "Structure check: an introduction; for each sub-issue Issue, Rule, Application, opposing case, rebuttal and sub-conclusion; then a conclusion.",
            "A structure that holds",
          ],
          [
            "4 · Draft review",
            "Paste your own draft. Durmah runs a reasoning-move checklist over each paragraph and returns the gaps as questions.",
            "A specific list of what to fix",
          ],
          [
            "5 · Formatting",
            "A doctrinal pre-flight before citation work, then OSCOLA help.",
            "Correct citations",
          ],
          [
            "6 · Final check",
            "Word count, OSCOLA, house style, plagiarism check, AI declaration, deadline — then, optionally, a full Mark My Paper diagnostic.",
            "A final pre-submission checklist",
          ],
        ],
      },
      {
        kind: "para",
        text: "Progress saves automatically and you can return to any stage. You can run the workflow at a normal or an express pace — that changes the pacing, not the rigour.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Do not skip stage one",
        text: "The most common cause of a disappointing mark is answering a question adjacent to the one that was asked. Stage one exists to stop that, and it is the stage students are most tempted to rush.",
      },
    ],
    related: ["source-pack-and-argument-map", "mark-my-paper", "academic-integrity-boundary"],
  },

  {
    slug: "source-pack-and-argument-map",
    title: "Source Pack and Argument Map",
    summary:
      "Two structured tools that sit alongside the workflow — a proper record of what you are relying on, and your reasoning as a structure you can see.",
    category: "assignments",
    audience: "public",
    order: 2,
    readTime: "3 min read",
    tags: ["Assignments", "Tools"],
    keywords: [
      "source pack",
      "argument map",
      "sources",
      "references",
      "keep track of cases",
      "structure my argument",
      "counterargument",
    ],
    blocks: [
      {
        kind: "heading",
        id: "source-pack",
        text: "Source Pack",
      },
      {
        kind: "para",
        text: "A record of what you are relying on. Each entry has a type — case, statute, textbook, journal article, lecture material, official guidance, website or other — with a title, a citation or link, and your own note.",
      },
      {
        kind: "quote",
        attribution: "Shown on every Source Pack",
        text: "These labels describe the source category. They are not an endorsement, and do not mean a source is correct, relevant, or verified.",
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "Durmah organises; you verify",
        text: "Durmah does not fetch or parse the links you save, and it does not vouch for what is behind them. Verifying a source remains your job — and it is the part of legal research that actually builds judgement.",
      },
      {
        kind: "heading",
        id: "argument-map",
        text: "Argument Map",
      },
      {
        kind: "para",
        text: "Your reasoning laid out as a structure you can see. Nodes can be a central question, a position, a proposition, a reason, a counterargument, a response, an unresolved question or a conclusion — connected by supports, challenges or responds-to relationships.",
      },
      {
        kind: "quote",
        attribution: "Shown at the foot of every Argument Map",
        text: "This is a structure for your own reasoning. Durmah does not create the propositions, arguments, sources, or conclusion for you. Your final argument and wording must be your own.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Build the counterarguments first",
        text: "Most students map their own argument and stop. The marks are in the response nodes — where you name the strongest objection and answer it. If a branch has no counterargument, you have probably not finished thinking about it.",
      },
    ],
    related: ["assignment-workflow", "research-hub", "mark-my-paper"],
  },

  {
    slug: "rubric-signal",
    title: "The rubric alignment signal",
    summary:
      "A diagnostic read on how your work aligns with assessment criteria — and a clear statement of what it is not.",
    category: "assignments",
    audience: "public",
    order: 3,
    readTime: "2 min read",
    tags: ["Assignments", "Feedback"],
    keywords: [
      "rubric",
      "marking criteria",
      "what grade will i get",
      "predicted grade",
      "will this get a first",
      "assessment criteria",
    ],
    blocks: [
      {
        kind: "para",
        text: "Durmah can give you a diagnostic read on how your work aligns with assessment criteria. Read carefully what that is.",
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "It is alignment feedback, not a mark",
        text: "It will not tell you your work 'is a First', 'would be marked as' anything, or that it 'cannot reach' a classification. It carries no predicted grade and no marker's decision, because Durmah is not your marker and cannot be.",
      },
      {
        kind: "para",
        text: "This is a checkpoint, not something to run after every paragraph — and it is rate-limited accordingly. Use it when you have a substantial draft and want to know which criterion is weakest, not as a scoreboard.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The useful question",
        text: "Do not ask 'what would this get?'. Ask 'which criterion is weakest, and what would move it?'. The first question has no honest answer. The second one has a specific, actionable one.",
      },
    ],
    related: ["assignment-workflow", "mark-my-paper", "reading-your-feedback"],
  },

  /* ---------------------------------------------------------------- */
  /* Mark My Paper                                                     */
  /* ---------------------------------------------------------------- */

  {
    slug: "mark-my-paper",
    title: "Mark My Paper: diagnostic review of your own draft",
    summary:
      "Give it your draft and it returns structured feedback — what is working, what is weak, what to fix first, and how to spend your next two hours.",
    category: "mark-my-paper",
    audience: "public",
    order: 1,
    readTime: "4 min read",
    tags: ["Mark My Paper", "Flagship"],
    keywords: [
      "mark my paper",
      "mmp",
      "feedback on my essay",
      "review my draft",
      "check my essay",
      "essay feedback",
      "proofread",
      "will you mark my work",
    ],
    blocks: [
      {
        kind: "callout",
        tone: "boundary",
        title: "A diagnostic, not a mark",
        text: "Durmah MMP provides diagnostic feedback only. It is not a university result forecast or a substitute for university marking. There is no human marker behind it — review means the AI diagnostic pipeline, and the product says so plainly rather than implying otherwise.",
      },
      {
        kind: "heading",
        id: "two-routes",
        text: "Two routes in",
      },
      {
        kind: "para",
        text: "Mark My Paper has two entry points, and picking the right one matters.",
      },
      {
        kind: "table",
        headers: ["Route", "Use it when", "What it gives you"],
        rows: [
          [
            "Question Paper First",
            "You have a paper or brief but have not written anything yet.",
            "Paper rules, a comparison of the question choices, how each maps to your own lectures, and planning prompts.",
          ],
          [
            "Mark my draft answer",
            "You have written something.",
            "The full diagnostic report — overview, question analysis, marked script, development plan, priority fixes and a revision plan.",
          ],
        ],
      },
      {
        kind: "heading",
        id: "using-it",
        text: "Running a review",
      },
      {
        kind: "steps",
        items: [
          "Choose your route.",
          "Provide the paper or the draft — paste the text, or upload a PDF, DOCX or TXT file.",
          "For Question Paper First, pick the question or questions you will answer.",
          "Submit. A full review runs in the background, so you can navigate away and come back.",
          "Read the report, then save it to your workspace — or discard it.",
        ],
      },
      {
        kind: "callout",
        tone: "note",
        title: "Discard means discard",
        text: "If you discard a review, nothing is written to your records and no memory of it is created. Use it freely on a rough draft you would rather not keep.",
      },
      {
        kind: "callout",
        tone: "safety",
        title: "It reviews your work — it does not write it",
        text: "Question Paper First helps you understand the paper and plan your approach. It does not write answers for submission. You remain responsible for your own answer, your own authorities, and your own academic integrity.",
      },
    ],
    related: ["reading-your-feedback", "mmp-limits", "assignment-workflow"],
  },

  {
    slug: "reading-your-feedback",
    title: "Reading your Mark My Paper report",
    summary:
      "Eleven sections, in a deliberate order. Here is what each one is for and which to read first when you are short of time.",
    category: "mark-my-paper",
    audience: "public",
    order: 2,
    readTime: "4 min read",
    tags: ["Mark My Paper", "Feedback"],
    keywords: [
      "feedback report",
      "marked script",
      "priority fixes",
      "development plan",
      "two hour plan",
      "diagnostic benchmark",
      "what do the sections mean",
      "criteria",
    ],
    blocks: [
      {
        kind: "table",
        headers: ["#", "Section", "What it gives you"],
        rows: [
          [
            "1",
            "Diagnostic and integrity notices",
            "What this report is and is not. Read it once, properly.",
          ],
          [
            "2",
            "Question",
            "The question the review ran against. Check this is the right one.",
          ],
          [
            "3",
            "Diagnostic benchmark",
            "A diagnostic profile with an indicative range, a confidence level, the reasoning, and what the next band would require. Explicitly not a mark.",
          ],
          [
            "4",
            "Overview",
            "Your top strengths, your top revision priorities, the single highest-impact fix, and a two-hour plan.",
          ],
          [
            "5",
            "Question analysis",
            "What the question is actually asking, and whether your answer engaged with it.",
          ],
          [
            "6",
            "Marked script",
            "Paragraph-by-paragraph comments on your own text.",
          ],
          ["7", "Development plan", "How to move the work forward."],
          ["8", "Priority fixes", "Ordered by impact. Start at the top."],
          [
            "9",
            "Detailed criteria feedback",
            "Per-criterion reasoning and improvements.",
          ],
          ["10", "Revision plan", "What to do next, concretely."],
          ["11", "Final disclaimer", "The boundary, restated."],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "If you read only one section",
        text: "Read the Overview. The two-hour plan and the single highest-impact fix are where the value is concentrated. The marked script is for a second pass, once you know what you are looking for.",
      },
      {
        kind: "heading",
        id: "criteria",
        text: "Which criteria are used",
      },
      {
        kind: "para",
        text: "Where your module has verified marking criteria, Durmah marks against those and stamps the report with which standard it used and when it was last reviewed. Where it does not, it uses five general criteria — legal issue identification, doctrinal accuracy and use of authority, application to the facts, structure and argumentation, and writing quality — and tells you it is doing so.",
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "It will not claim standards it does not have",
        text: "If Durmah has not verified your module's marking criteria, it says it is using general criteria rather than implying it knows your department's rubric. That honesty is deliberate, and it is why you can trust the times it does claim module-specific standards.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "If confidence is shown as low",
        text: "It means the full review could not complete and the benchmark is provisional. Retry rather than acting on it.",
      },
    ],
    related: ["mark-my-paper", "mmp-limits", "rubric-signal"],
  },

  {
    slug: "mmp-limits",
    title: "Mark My Paper: formats, lengths and troubleshooting",
    summary:
      "Accepted file types, how long an answer can be, and what to do when a review will not run.",
    category: "mark-my-paper",
    audience: "member",
    order: 3,
    readTime: "3 min read",
    tags: ["Mark My Paper", "Limits", "Member"],
    keywords: [
      "file too big",
      "answer too long",
      "unsupported file",
      "cannot read file",
      "review stuck",
      "mmp error",
      "word limit",
      "how long can my answer be",
    ],
    blocks: [
      {
        kind: "table",
        headers: ["Item", "Limit"],
        rows: [
          ["Accepted formats", "PDF, DOCX and TXT"],
          ["Upload size", "Up to 10 MB"],
          ["Minimum draft", "At least 50 characters"],
          [
            "Maximum per run",
            "28,000 characters. Beyond that, split the paper by question, issue or section and run each part.",
          ],
          ["Paper word limit", "Between 100 and 50,000 words"],
          ["Number of reviews", "No per-user quota"],
        ],
      },
      {
        kind: "para",
        text: "Long answers are reviewed in full, but Durmah tells you honestly when it is prioritising. Between roughly 18,000 and 22,000 characters it focuses on the most important revision priorities; above that it prioritises the highest-impact issues rather than commenting on every paragraph.",
      },
      {
        kind: "heading",
        id: "troubleshooting",
        text: "When something goes wrong",
      },
      {
        kind: "table",
        headers: ["Problem", "What to do"],
        rows: [
          [
            "We could not read that file",
            "Save it as a DOCX, or paste the text directly.",
          ],
          [
            "Question Analysis unavailable",
            "Upload or paste the question paper first — it needs the question to analyse.",
          ],
          [
            "The review seems stuck",
            "Full reviews run in the background. Give it a few minutes, then retry.",
          ],
          [
            "Feedback references the wrong question",
            "Reopen the paper from the workspace, re-select the question, and run the review again.",
          ],
          [
            "The answer is too long",
            "Split by question or issue and run each part separately.",
          ],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Run it twice",
        text: "Run a review, act on the priority fixes, then run it again. Watching which priorities disappear — and which stubbornly do not — tells you more about your writing than either report alone.",
      },
    ],
    related: ["mark-my-paper", "reading-your-feedback", "getting-help"],
  },
];
